import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api } from "../api.js";
import { formatRand } from "../prices/Prices.js";
import { useSelectedTenant } from "../shell/tenant.js";
import { FieldReason, ifMatch, RefusalNotice, toRefusal, type Refusal } from "../zd/refusal.js";

// S-051 Z04 Payments and credits: invoices (spec 4.3 Z04, BILL-06, BILL-09, BILL-10, NFR-16).
// Lists invoices with amounts excluding and including VAT on every row (BILL-09, ST-067); records
// an invoice for the selected tenant at the price in effect today; marks an invoice paid with the
// payment date and bank reference, or cancels it with a reason. The server decides everything and
// the screen shows its reasons (ZD screen error pattern, refusal.tsx). Each open form holds one
// request id (Idempotency-Key): a second press, or a retry after a lost connection, sends the same
// id, so the payment is recorded once (NFR-16). Adjust credits and billing requests are later
// stories (S-056 lists billing requests here).

type Module = "lite" | "remembrance";
type Status = "issued" | "paid" | "cancelled";

export interface InvoiceItem {
  id: string;
  tenantId: string;
  tenantName: string;
  module: Module;
  number: string;
  amountExclVatCents: number;
  vatCents: number;
  amountInclVatCents: number;
  vatRateBasisPoints: number | null;
  issueDate: string;
  dueDate: string;
  creditsQuantity: number | null;
  periodStart: string | null;
  periodEnd: string | null;
  status: Status;
  paymentDate: string | null;
  bankReference: string | null;
  cancelledReason: string | null;
  version: number;
}

interface PriceForInvoice {
  priceSettingId: string;
  priceGroup: string | null;
  amountExclVatCents: number;
  amountInclVatCents: number | null;
}

export interface InvoicesAnswer {
  tenant: { id: string; name: string } | null;
  today: string;
  vatRateBasisPoints: number | null;
  prices: Record<Module, PriceForInvoice | null> | null;
  subscription: { status: SubscriptionStatus; periodStart: string | null; periodEnd: string | null } | null;
  creditBalance: number | null;
  invoices: InvoiceItem[];
}

/** A fresh request id for one form (NFR-16). */
export function newRequestId(): string {
  return `z04-${crypto.randomUUID()}`;
}

const STATUS_KEY = {
  issued: "console.invoices.status_issued",
  paid: "console.invoices.status_paid",
  cancelled: "console.invoices.status_cancelled",
} as const;

type SubscriptionStatus = "trial" | "active" | "past_due" | "suspended" | "cancelled";
const SUBSCRIPTION_KEY = {
  trial: "console.invoices.subscription_trial",
  active: "console.invoices.subscription_active",
  past_due: "console.invoices.subscription_past_due",
  suspended: "console.invoices.subscription_suspended",
  cancelled: "console.invoices.subscription_cancelled",
} as const;

/** VAT on an amount at a rate in basis points, as the server rounds it (BILL-09). */
export const vatOf = (exclCents: number, basisPoints: number) => Math.round((exclCents * basisPoints) / 10000);

function detail(i: InvoiceItem): string {
  if (i.module === "remembrance") return `${t("console.invoices.credits")} ${i.creditsQuantity}`;
  return `${t("console.invoices.period")} ${i.periodStart} ${t("console.invoices.period_to")} ${i.periodEnd}`;
}

function MarkPaid({ invoice, today, onDone, onReload }: { invoice: InvoiceItem; today: string; onDone: () => void; onReload: () => void }) {
  const [paymentDate, setPaymentDate] = useState(today);
  const [bankReference, setBankReference] = useState("");
  const [requestId] = useState(newRequestId);
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const r = await api(`/v1/zd/invoices/${invoice.id}/actions/mark-paid`, {
      method: "POST",
      headers: { ...ifMatch(invoice.version), "Idempotency-Key": requestId },
      body: { paymentDate, bankReference },
    });
    setBusy(false);
    const refused = toRefusal(r);
    setRefusal(refused);
    if (!refused) onDone();
  }

  return (
    <form onSubmit={submit} noValidate aria-label={`${t("console.invoices.mark_paid_title")} ${invoice.number}`}>
      <RefusalNotice refusal={refusal} onReload={onReload} />
      <div className="field">
        <label>
          {t("console.invoices.field_payment_date")}
          <input name="paymentDate" type="date" max={today} value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
        </label>
        <FieldReason refusal={refusal} field="paymentDate" />
      </div>
      <div className="field">
        <label>
          {t("console.invoices.field_bank_reference")}
          <input name="bankReference" maxLength={60} value={bankReference} onChange={(e) => setBankReference(e.target.value)} />
        </label>
        <FieldReason refusal={refusal} field="bankReference" />
      </div>
      <button type="submit" disabled={busy}>
        {busy ? t("console.invoices.saving") : t("console.invoices.confirm_paid")}
      </button>
    </form>
  );
}

function CancelInvoice({ invoice, onDone, onReload }: { invoice: InvoiceItem; onDone: () => void; onReload: () => void }) {
  const [reason, setReason] = useState("");
  const [requestId] = useState(newRequestId);
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    const r = await api(`/v1/zd/invoices/${invoice.id}/actions/cancel`, {
      method: "POST",
      headers: { ...ifMatch(invoice.version), "Idempotency-Key": requestId },
      body: { reason },
    });
    setBusy(false);
    const refused = toRefusal(r);
    setRefusal(refused);
    if (!refused) onDone();
  }

  return (
    <form onSubmit={submit} noValidate aria-label={`${t("console.invoices.cancel_title")} ${invoice.number}`}>
      <RefusalNotice refusal={refusal} onReload={onReload} />
      <div className="field">
        <label>
          {t("console.invoices.field_reason")}
          <input name="reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>
        <FieldReason refusal={refusal} field="reason" />
      </div>
      <button type="submit" disabled={busy}>
        {busy ? t("console.invoices.saving") : t("console.invoices.confirm_cancel")}
      </button>
    </form>
  );
}

function InvoiceTable({ data, onChanged }: { data: InvoicesAnswer; onChanged: () => void }) {
  const [open, setOpen] = useState<{ id: string; action: "pay" | "cancel" } | undefined>();
  const [done, setDone] = useState<string | undefined>();
  const showTenant = data.tenant === null;
  if (data.invoices.length === 0) return <p>{t("console.invoices.none")}</p>;
  const finish = (message: string) => {
    setOpen(undefined);
    setDone(message);
    onChanged();
  };
  return (
    <>
      {done && <p role="status">{done}</p>}
      <table aria-label={t("console.invoices.list_title")}>
        <thead>
          <tr>
            <th scope="col">{t("console.invoices.col_number")}</th>
            {showTenant && <th scope="col">{t("console.invoices.col_tenant")}</th>}
            <th scope="col">{t("console.invoices.col_module")}</th>
            <th scope="col">{t("console.invoices.col_detail")}</th>
            <th scope="col">{t("console.invoices.col_issued")}</th>
            <th scope="col">{t("console.invoices.col_due")}</th>
            <th scope="col">{t("console.invoices.col_excl_vat")}</th>
            <th scope="col">{t("console.invoices.col_vat")}</th>
            <th scope="col">{t("console.invoices.col_incl_vat")}</th>
            <th scope="col">{t("console.invoices.col_status")}</th>
            <th scope="col">{t("console.invoices.col_actions")}</th>
          </tr>
        </thead>
        <tbody>
          {data.invoices.map((i) => (
            <tr key={i.id}>
              <td>{i.number}</td>
              {showTenant && <td>{i.tenantName}</td>}
              <td>{i.module === "lite" ? t("console.invoices.module_lite") : t("console.invoices.module_remembrance")}</td>
              <td>{detail(i)}</td>
              <td>{i.issueDate}</td>
              <td>{i.dueDate}</td>
              <td>{formatRand(i.amountExclVatCents)}</td>
              <td>{formatRand(i.vatCents)}</td>
              <td>{formatRand(i.amountInclVatCents)}</td>
              <td>
                {t(STATUS_KEY[i.status])}
                {i.status === "paid" && (
                  <span className="hint">
                    {" "}
                    {i.paymentDate} {i.bankReference}
                  </span>
                )}
                {i.status === "cancelled" && <span className="hint"> {i.cancelledReason}</span>}
              </td>
              <td>
                {i.status === "issued" && (
                  <>
                    <button type="button" onClick={() => setOpen({ id: i.id, action: "pay" })}>
                      {t("console.invoices.mark_paid")}
                    </button>{" "}
                    <button type="button" onClick={() => setOpen({ id: i.id, action: "cancel" })}>
                      {t("console.invoices.cancel")}
                    </button>
                    {open?.id === i.id && open.action === "pay" && (
                      <MarkPaid invoice={i} today={data.today} onDone={() => finish(t("console.invoices.paid_done"))} onReload={onChanged} />
                    )}
                    {open?.id === i.id && open.action === "cancel" && (
                      <CancelInvoice invoice={i} onDone={() => finish(t("console.invoices.cancel_done"))} onReload={onChanged} />
                    )}
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

interface Form {
  module: Module;
  creditsQuantity: string;
  periodStart: string;
  dueDate: string;
}

function RecordInvoice({ data, onRecorded }: { data: InvoicesAnswer; onRecorded: () => void }) {
  const [form, setForm] = useState<Form>({ module: "remembrance", creditsQuantity: "", periodStart: data.today, dueDate: data.today });
  const [requestId, setRequestId] = useState(newRequestId);
  const [refusal, setRefusal] = useState<Refusal | undefined>();
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<string | undefined>();
  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    // A changed form is a new request.
    setRequestId(newRequestId());
  };

  const price = data.prices?.[form.module] ?? null;
  const rate = data.vatRateBasisPoints;
  const quantity = /^\d{1,4}$/.test(form.creditsQuantity) ? Number(form.creditsQuantity) : undefined;
  const excl = price ? (form.module === "lite" ? price.amountExclVatCents : quantity !== undefined ? price.amountExclVatCents * quantity : undefined) : undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setSaved(undefined);
    const r = await api<{ invoice: { number: string } }>("/v1/zd/invoices", {
      method: "POST",
      headers: { "Idempotency-Key": requestId },
      body: {
        tenantId: data.tenant!.id,
        module: form.module,
        dueDate: form.dueDate,
        ...(form.module === "remembrance" ? { creditsQuantity: quantity ?? form.creditsQuantity } : { periodStart: form.periodStart }),
      },
    });
    setBusy(false);
    const refused = toRefusal(r);
    setRefusal(refused);
    if (r.ok) {
      setSaved(`${t("console.invoices.recorded")} ${r.data.invoice.number}`);
      setForm((f) => ({ ...f, creditsQuantity: "" }));
      setRequestId(newRequestId());
      onRecorded();
    }
  }

  return (
    <form onSubmit={submit} noValidate aria-label={t("console.invoices.record_title")}>
      <h3>{t("console.invoices.record_title")}</h3>
      <RefusalNotice refusal={refusal} />
      {saved && <p role="status">{saved}</p>}
      <div className="field">
        <label>
          {t("console.invoices.field_module")}
          <select name="module" value={form.module} onChange={(e) => set("module", e.target.value as Module)}>
            <option value="remembrance">{t("console.invoices.module_remembrance")}</option>
            <option value="lite">{t("console.invoices.module_lite")}</option>
          </select>
        </label>
        <FieldReason refusal={refusal} field="module" />
      </div>
      {form.module === "remembrance" ? (
        <div className="field">
          <label>
            {t("console.invoices.field_credits")}
            <input name="creditsQuantity" inputMode="numeric" maxLength={4} value={form.creditsQuantity} onChange={(e) => set("creditsQuantity", e.target.value)} />
          </label>
          <FieldReason refusal={refusal} field="creditsQuantity" />
        </div>
      ) : (
        <div className="field">
          <label>
            {t("console.invoices.field_period_start")}
            <input name="periodStart" type="date" value={form.periodStart} onChange={(e) => set("periodStart", e.target.value)} />
          </label>
          <p className="hint">{t("console.invoices.field_period_hint")}</p>
          <FieldReason refusal={refusal} field="periodStart" />
        </div>
      )}
      <div className="field">
        <label>
          {t("console.invoices.field_due_date")}
          <input name="dueDate" type="date" min={data.today} value={form.dueDate} onChange={(e) => set("dueDate", e.target.value)} />
        </label>
        <FieldReason refusal={refusal} field="dueDate" />
      </div>
      <dl data-testid="invoice-preview">
        <dt>{t("console.invoices.preview_price")}</dt>
        <dd>
          {price ? (
            <>
              {price.priceGroup && <strong>{price.priceGroup} </strong>}
              {formatRand(price.amountExclVatCents)} {t("console.invoices.excl_vat")}
            </>
          ) : (
            t("console.invoices.no_price")
          )}
        </dd>
        <dt>{t("console.invoices.col_excl_vat")}</dt>
        <dd>{excl === undefined ? "" : formatRand(excl)}</dd>
        <dt>{t("console.invoices.col_vat")}</dt>
        <dd>{rate === null ? t("console.invoices.vat_not_set") : excl === undefined ? "" : formatRand(vatOf(excl, rate))}</dd>
        <dt>{t("console.invoices.col_incl_vat")}</dt>
        <dd>{rate === null ? t("console.invoices.vat_not_set") : excl === undefined ? "" : formatRand(excl + vatOf(excl, rate))}</dd>
      </dl>
      <button type="submit" disabled={busy}>
        {busy ? t("console.invoices.saving") : t("console.invoices.record_submit")}
      </button>
    </form>
  );
}

export function Invoices() {
  const { selected } = useSelectedTenant();
  // The tenant's id, not the object: the tenant bar refreshes its list without changing the choice.
  const selectedId = selected?.id ?? null;
  // The answer, with the tenant it was asked for, so it is never shown under another choice.
  const [data, setData] = useState<{ forTenant: string | null; answer: InvoicesAnswer } | undefined>();
  const [loadError, setLoadError] = useState<string | undefined>();

  // Only the latest request's answer is shown: an answer for a tenant no longer chosen is dropped.
  const latest = useRef(0);

  const load = useCallback(async () => {
    const n = ++latest.current;
    const r = await api<InvoicesAnswer>(selectedId ? `/v1/zd/invoices?tenant=${encodeURIComponent(selectedId)}` : "/v1/zd/invoices");
    if (n !== latest.current) return;
    if (r.ok) {
      setData({ forTenant: selectedId, answer: r.data });
      setLoadError(undefined);
    } else {
      setLoadError(r.status === 0 ? t("common.errors.no_connection") : (r.error?.message ?? t("console.invoices.load_failed")));
    }
  }, [selectedId]);

  useEffect(() => {
    setData(undefined);
    void load();
  }, [load]);

  const current = data && data.forTenant === selectedId ? data.answer : undefined;
  return (
    <section aria-labelledby="invoices-title">
      <h2 id="invoices-title">{t("console.invoices.title")}</h2>
      <p>{t("console.invoices.intro")}</p>
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {!current && !loadError && <p aria-busy="true">{t("console.invoices.loading")}</p>}
      {current && (
        <>
          {current.tenant ? (
            <dl data-testid="tenant-billing">
              <dt>{t("console.invoices.credit_balance")}</dt>
              <dd>{current.creditBalance}</dd>
              <dt>{t("console.invoices.subscription")}</dt>
              <dd>
                {current.subscription
                  ? `${t(SUBSCRIPTION_KEY[current.subscription.status])} ${current.subscription.periodStart ?? ""} ${t("console.invoices.period_to")} ${current.subscription.periodEnd ?? ""}`
                  : t("console.invoices.no_subscription")}
              </dd>
            </dl>
          ) : (
            <p>{t("console.invoices.choose_tenant")}</p>
          )}
          <h3>{t("console.invoices.list_title")}</h3>
          <InvoiceTable data={current} onChanged={() => void load()} />
          {current.tenant && <RecordInvoice key={current.tenant.id} data={current} onRecorded={() => void load()} />}
        </>
      )}
    </section>
  );
}
